import React, { useState, useEffect } from 'react';

interface TimerProps {
  event: any;
}

const Timer: React.FC<TimerProps> = ({ event }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [isEventLive, setIsEventLive] = useState(false);
  const [isEventEnded, setIsEventEnded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      try {
        const now = new Date().getTime();
        const startTime = new Date(event.starts).getTime();
        const endTime = event.ends ? new Date(event.ends).getTime() : null;

        // Validate dates
        if (isNaN(startTime) || (endTime && isNaN(endTime))) {
          console.error('Invalid date format in timer');
          setHasError(true);
          setIsLoading(false);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        // Check if event has ended
        if (endTime && now > endTime) {
          setIsEventEnded(true);
          setIsEventLive(false);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        // Check if event is live
        if (now >= startTime && (!endTime || now < endTime)) {
          setIsEventLive(true);
          if (endTime) {
            // Countdown to event end
            const difference = Math.max(0, endTime - now);
            return {
              days: Math.floor(difference / (1000 * 60 * 60 * 24)),
              hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
              minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
              seconds: Math.floor((difference % (1000 * 60)) / 1000)
            };
          }
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        } else {
          // Countdown to event start
          setIsEventLive(false);
          const difference = Math.max(0, startTime - now);
          return {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((difference % (1000 * 60)) / 1000)
          };
        }
      } catch (error) {
        console.error('Error calculating time left:', error);
        setHasError(true);
        setIsLoading(false);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
    };

    const updateTimer = () => {
      setTimeLeft(calculateTimeLeft());
      setIsLoading(false);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [event.starts, event.ends]);

  if (hasError) {
    return (
      <div className="timer">
        <div className="time-counter">
          <div className="event-status">
            <h5 className="text-muted mb-0">Timer Error</h5>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="timer">
        <div className="time-counter">
          <div className="event-status">
            <h5 className="text-muted mb-0">Loading...</h5>
          </div>
        </div>
      </div>
    );
  }

  if (isEventEnded) {
    return (
      <div className="timer">
        <div className="time-counter">
          <div className="event-status">
            <h5 className="text-muted mb-0">Event Ended</h5>
          </div>
        </div>
      </div>
    );
  }

        return (
          <div className="timer">
            <div className="time-counter">
              <div className="countdown-display">
                <div className="time-unit">
                  <span className="time-value">{timeLeft.days.toString().padStart(2, '0')}</span>
                  <span className="time-label">D</span>
                </div>
                <div className="time-separator">:</div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.hours.toString().padStart(2, '0')}</span>
                  <span className="time-label">H</span>
                </div>
                <div className="time-separator">:</div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.minutes.toString().padStart(2, '0')}</span>
                  <span className="time-label">M</span>
                </div>
                <div className="time-separator">:</div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.seconds.toString().padStart(2, '0')}</span>
                  <span className="time-label">S</span>
                </div>
              </div>
            </div>
          </div>
        );
};

export default Timer;
